// SPDX-License-Identifier: Apache-2.0

// xlsx (and legacy .xls) → markdown.
//
// Each sheet becomes its own H1 section. Empty sheets (zero non-blank rows)
// are skipped. Cells are rendered through the same csv→markdown pipe-table as
// `csv.ts` so the SPA's note viewer renders them consistently.
//
// Library: SheetJS's `xlsx` package on npm. We import the namespace with
// `read`/`utils`, mirroring the old Tether wiring. The package name is
// literally `xlsx` (not `@sheetjs/sheetjs` — that's the paid tarball channel).
//
// We use `sheet_to_json` with `header:1` to get a `string[][]` — a simple,
// predictable shape. `defval:''` fills holes so the resulting GFM table
// stays a grid.

import {
  baseTitle,
  corruptResult,
  warnIfLarge,
  type IngestResult,
} from './common';
import { rowsToMarkdownTable } from './csv';

interface XlsxSheet {
  // Opaque shape — utils.sheet_to_json reads it.
}

interface XlsxWorkbook {
  SheetNames: string[];
  Sheets: Record<string, XlsxSheet>;
}

interface XlsxModule {
  read(data: ArrayBuffer | Uint8Array, opts: { type: 'array' | 'buffer' }): XlsxWorkbook;
  utils: {
    sheet_to_json<T = unknown[]>(sheet: XlsxSheet, opts?: {
      header?: 1 | string[];
      defval?: unknown;
      raw?: boolean;
      blankrows?: boolean;
    }): T[];
  };
}

export async function parseXlsx(file: File): Promise<IngestResult> {
  warnIfLarge(file, 'xlsx');
  const title = baseTitle(file.name);
  try {
    const mod = await import('xlsx');
    const xlsx = ((mod as unknown as { default?: XlsxModule }).default ??
      (mod as unknown as XlsxModule));
    const arrayBuffer = await file.arrayBuffer();
    const wb = xlsx.read(arrayBuffer, { type: 'array' });

    const sections: string[] = [];
    sections.push(`# ${title.replace(/\.md$/i, '')}`);

    for (const name of wb.SheetNames) {
      const sheet = wb.Sheets[name];
      if (!sheet) continue;
      const rowsRaw = xlsx.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        defval: '',
        raw: false,
        blankrows: false,
      });
      // Coerce every cell to string. `sheet_to_json` returns a 2D array when
      // `header:1`, but TypeScript can't narrow that without help.
      const rows: string[][] = rowsRaw.map((r) =>
        Array.isArray(r) ? r.map((c) => stringifyCell(c)) : [],
      );
      const nonEmpty = rows.filter((r) => r.some((c) => c.trim() !== ''));
      if (nonEmpty.length === 0) continue; // skip empty sheets

      const table = rowsToMarkdownTable(nonEmpty);
      sections.push(`## ${name}\n\n${table}`);
    }
    return {
      title,
      content: `${sections.join('\n\n')}\n`,
      front: {},
      kind: 'xlsx',
    };
  } catch (err) {
    return corruptResult(file.name, err instanceof Error ? err.message : String(err));
  }
}

function stringifyCell(c: unknown): string {
  if (c === null || c === undefined) return '';
  if (typeof c === 'string') return c;
  if (typeof c === 'number' || typeof c === 'boolean') return String(c);
  if (c instanceof Date) return c.toISOString();
  return String(c);
}
